import { Link, useParams } from 'react-router-dom'
import { Icon } from '../components/Icon'
import { ProductCardGrid } from '../components/ProductCardGrid'
import { ProductGridSkeleton, ProductPageSkeleton } from '../components/Skeleton'
import { CART_PATHS, CART_UI } from '../constants/cart'
import { ICON_FALLBACK, RATING_STAR_SIZE } from '../constants/icons'
import {
  PRODUCT_INFO_SECTIONS,
  PRODUCT_OPTION_SECTIONS,
  PRODUCT_PAGE_UI,
  PRODUCT_RELATED,
} from '../constants/product'
import { SKELETON_COUNTS } from '../constants/skeleton'
import { cx, formatAddonPrice, formatCurrency } from '../helpers/format'
import { ratingStars } from '../helpers/rating'
import { useProductPage } from '../hooks/useProductPage'
import { useRelatedProducts } from '../hooks/useRelatedProducts'
import '../styles/home-page.scss'
import '../styles/product-page.scss'

export function ProductPage() {
  const { slug } = useParams<{ slug: string }>()
  const {
    product,
    loading,
    error,
    imageId,
    setImageId,
    galleryImages,
    stemId,
    setStemId,
    wrapperId,
    setWrapperId,
    addonIds,
    toggleAddon,
    wishlisted,
    toggleWishlist,
    wishlistBusy,
    compareAt,
    total,
    savingsPercent,
    selectedSku,
    selectedContent,
    addToCart,
    buyNow,
    justAdded,
    productInCartQty,
  } = useProductPage(slug)
  const related = useRelatedProducts(product)

  if (loading) {
    return <ProductPageSkeleton />
  }

  if (error || !product) {
    return (
      <div className="product-page">
        <div className="container">
          <p className="catalog-status catalog-status--error">
            {error ?? PRODUCT_PAGE_UI.notFound}
          </p>
          <Link to="/" className="product-back-link">
            {PRODUCT_PAGE_UI.backHome}
          </Link>
        </div>
      </div>
    )
  }

  const activeImage = galleryImages.find((image) => image.id === imageId) ?? galleryImages[0]
  const content = selectedContent ?? product.content
  const hasInfoSections =
    content.contains.length > 0 ||
    Boolean(content.description?.trim()) ||
    content.careInstructions.length > 0 ||
    Boolean(selectedSku)

  return (
    <div className="product-page">
      <div className="container">
        <nav className="product-breadcrumbs" aria-label="Breadcrumb">
          {product.breadcrumbs.map((crumb, index) => (
            <span key={crumb.label} className="product-breadcrumbs__item">
              {index > 0 ? <span aria-hidden>›</span> : null}
              {index === product.breadcrumbs.length - 1 ? (
                <span aria-current="page">{crumb.label}</span>
              ) : (
                <Link to={crumb.to}>{crumb.label}</Link>
              )}
            </span>
          ))}
        </nav>

        <div className="product-layout">
          <div className="product-media">
            <div className={cx('product-gallery', activeImage ? `tone-${activeImage.tone}` : 'tone-sage')}>
              {product.badges.length > 0 ? (
                <div className="product-gallery__badges">
                  {product.badges.map((badge) => (
                    <span
                      key={badge.label}
                      className={cx('product-badge', `product-badge--${badge.tone}`)}
                    >
                      {badge.icon ? <Icon name={badge.icon} size={13} filled /> : null}
                      {badge.label}
                    </span>
                  ))}
                </div>
              ) : null}

              <button
                type="button"
                className={cx('product-wishlist', wishlisted && 'is-active')}
                aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                aria-pressed={wishlisted}
                disabled={wishlistBusy}
                onClick={() => void toggleWishlist()}
              >
                <Icon name="heart" size={20} filled={wishlisted} />
              </button>

              {activeImage?.url ? (
                <img
                  src={activeImage.url}
                  alt={activeImage.label}
                  className="product-gallery__image"
                />
              ) : (
                <div className="product-gallery__art" aria-hidden>
                  <div className="product-gallery__bloom" />
                  <div className="product-gallery__petal product-gallery__petal--1" />
                  <div className="product-gallery__petal product-gallery__petal--2" />
                  <div className="product-gallery__stem" />
                </div>
              )}
            </div>

            {galleryImages.length > 0 ? (
              <div className="product-thumbs" role="list">
                {galleryImages.map((image) => (
                  <button
                    key={image.id}
                    type="button"
                    role="listitem"
                    className={cx(
                      'product-thumb',
                      `tone-${image.tone}`,
                      imageId === image.id && 'is-active',
                    )}
                    aria-label={image.label}
                    aria-pressed={imageId === image.id}
                    onClick={() => setImageId(image.id)}
                  >
                    {image.url ? (
                      <img src={image.url} alt="" className="product-thumb__image" />
                    ) : (
                      <span className="product-thumb__art" aria-hidden />
                    )}
                  </button>
                ))}
              </div>
            ) : null}

            {product.seller ? (
              <div className="product-seller">
                <div className="product-seller__avatar" aria-hidden>
                  {product.seller.initial}
                </div>
                <div className="product-seller__body">
                  <div className="product-seller__row">
                    <strong>{product.seller.name}</strong>
                    {product.seller.verified ? (
                      <span className="product-seller__verified">{PRODUCT_PAGE_UI.verified}</span>
                    ) : null}
                  </div>
                  {product.seller.rating != null ? (
                    <p>
                      <Icon name="star" size={RATING_STAR_SIZE} filled /> {product.seller.rating}
                      {product.seller.reviewCount > 0
                        ? ` · ${product.seller.reviewCount} ${PRODUCT_PAGE_UI.reviews}`
                        : null}
                    </p>
                  ) : null}
                  {product.seller.location ? (
                    <p className="product-seller__location">{product.seller.location}</p>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          <div className="product-details">
            <div className="product-details__meta">
              <span>{product.categoryPath}</span>
              {product.inStock ? (
                <span className="product-stock">
                  <span className="product-stock__dot" aria-hidden />
                      {PRODUCT_PAGE_UI.inStock}
                </span>
              ) : null}
            </div>

            <h1>{product.name}</h1>

            {content.shortDescription ? (
              <p className="product-details__summary">{content.shortDescription}</p>
            ) : null}

            {product.rating != null || product.tags.length > 0 ? (
              <div className="product-rating">
                {product.rating != null ? (
                  <>
                    <span className="product-rating__stars" aria-hidden>
                      {ratingStars(product.rating).map((star) => (
                        <Icon key={star.id} name="star" size={RATING_STAR_SIZE} filled={star.filled} />
                      ))}
                    </span>
                    <strong>{product.rating}</strong>
                    {product.reviewCount > 0 ? (
                      <span>
                        {product.reviewCount} {PRODUCT_PAGE_UI.reviews}
                      </span>
                    ) : null}
                  </>
                ) : null}
                {product.tags.map((tag) => (
                  <span key={tag} className="product-tag">
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}

            <div className="product-price">
              <div className="product-price__main">
                <strong>{formatCurrency(total)}</strong>
                {compareAt != null ? (
                  <span className="product-price__compare">{formatCurrency(compareAt)}</span>
                ) : null}
                {savingsPercent > 0 ? (
                  <span className="product-price__off">{savingsPercent}% off</span>
                ) : null}
              </div>
              <div className="product-price__notes">
                <span>{product.priceNote}</span>
              </div>
            </div>

            {product.stemOptions.length > 0 ? (
              <section className="product-section">
                <h2>{PRODUCT_OPTION_SECTIONS.stem}</h2>
                <div className="option-grid option-grid--stems">
                  {product.stemOptions.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={cx('option-card', stemId === option.id && 'is-selected')}
                      aria-pressed={stemId === option.id}
                      onClick={() => setStemId(option.id)}
                    >
                      <span>{option.label}</span>
                      <strong>{formatCurrency(option.price)}</strong>
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            {product.wrapperOptions.length > 0 ? (
              <section className="product-section">
                <h2>{PRODUCT_OPTION_SECTIONS.wrapper}</h2>
                <div className="option-grid option-grid--wrappers">
                  {product.wrapperOptions.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={cx(
                        'option-card option-card--rich',
                        wrapperId === option.id && 'is-selected',
                      )}
                      aria-pressed={wrapperId === option.id}
                      onClick={() => setWrapperId(option.id)}
                    >
                      {wrapperId === option.id ? (
                        <span className="option-card__check" aria-hidden>
                          <Icon name="check" size={12} strokeWidth={3} />
                        </span>
                      ) : null}
                      {option.icon ? (
                        <span className="option-card__icon" aria-hidden>
                          <Icon name={option.icon} size={22} />
                        </span>
                      ) : null}
                      <span className="option-card__label">{option.label}</span>
                      {option.description ? (
                        <span className="option-card__desc">{option.description}</span>
                      ) : null}
                      <span className="option-card__price">{formatAddonPrice(option.price)}</span>
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            {product.addonOptions.length > 0 ? (
              <section className="product-section">
                <h2>{PRODUCT_OPTION_SECTIONS.addons}</h2>
                <div className="option-grid option-grid--addons">
                  {product.addonOptions.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={cx(
                        'option-card option-card--rich',
                        addonIds.includes(option.id) && 'is-selected',
                      )}
                      aria-pressed={addonIds.includes(option.id)}
                      onClick={() => toggleAddon(option.id)}
                    >
                      {addonIds.includes(option.id) ? (
                        <span className="option-card__check" aria-hidden>
                          <Icon name="check" size={12} strokeWidth={3} />
                        </span>
                      ) : null}
                      {option.imageUrl ? (
                        <img src={option.imageUrl} alt="" className="option-card__photo" />
                      ) : (
                        <span className="option-card__icon" aria-hidden>
                          <Icon name={option.icon ?? ICON_FALLBACK.addon} size={22} />
                        </span>
                      )}
                      <span className="option-card__label">{option.label}</span>
                      <span className="option-card__price">{formatAddonPrice(option.price)}</span>
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            <div className="product-cta">
              <button
                type="button"
                className={cx(
                  'product-cta__btn',
                  'product-cta__btn--solid',
                  (justAdded || productInCartQty > 0) && 'is-added',
                )}
                onClick={addToCart}
              >
                {justAdded ? CART_UI.addedFeedback : product.cta.addToCart}
              </button>
              <button
                type="button"
                className="product-cta__btn product-cta__btn--outline"
                onClick={buyNow}
              >
                {product.cta.buyNow}
              </button>
            </div>

            {productInCartQty > 0 ? (
              <div className="product-cart-notice" role="status" aria-live="polite">
                <p>
                  <Icon name="check" size={14} strokeWidth={2.4} />{' '}
                  {justAdded ? CART_UI.addedFeedback : CART_UI.inCartFeedback}
                  {' '}
                  · {productInCartQty}{' '}
                  {productInCartQty === 1 ? CART_UI.itemSingular : CART_UI.itemPlural}
                </p>
                <Link to={CART_PATHS.page} className="product-cart-notice__link">
                  {CART_UI.viewCart}
                </Link>
              </div>
            ) : null}

            {hasInfoSections ? (
              <div className="product-info">
                {content.contains.length > 0 ? (
                  <section className="product-info__section">
                    <h2>{PRODUCT_INFO_SECTIONS.contains}</h2>
                    <ul className="product-info__list">
                      {content.contains.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </section>
                ) : null}

                {content.description?.trim() ? (
                  <section className="product-info__section">
                    <h2>{PRODUCT_INFO_SECTIONS.description}</h2>
                    <p className="product-info__body">{content.description}</p>
                  </section>
                ) : null}

                {content.careInstructions.length > 0 ? (
                  <section className="product-info__section">
                    <h2>{PRODUCT_INFO_SECTIONS.care}</h2>
                    <ul className="product-info__list product-info__list--care">
                      {content.careInstructions.map((item) => (
                        <li key={`${item.title ?? ''}-${item.text}`}>
                          {item.title ? <strong>{item.title}</strong> : null}
                          {item.title && item.text && item.title !== item.text ? ' ' : null}
                          {item.title !== item.text ? item.text : null}
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}

                {selectedSku ? (
                  <section className="product-info__section product-info__section--sku">
                    <h2>{PRODUCT_INFO_SECTIONS.sku}</h2>
                    <p className="product-info__sku">{selectedSku}</p>
                  </section>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {related.loading || related.products.length > 0 ? (
          <section className="product-related" aria-labelledby="product-related-heading">
            <header className="product-related__head">
              <h2 id="product-related-heading">{PRODUCT_RELATED.title}</h2>
              <p>{PRODUCT_RELATED.subtitle}</p>
            </header>
            {related.loading && related.products.length === 0 ? (
              <ProductGridSkeleton count={SKELETON_COUNTS.related} />
            ) : (
              <ProductCardGrid products={related.products} />
            )}
          </section>
        ) : null}
      </div>
    </div>
  )
}
